import './styles.scss';
import React, {Dispatch, useContext, useEffect, useState} from "react";

/* Types, Constants, Utils */
import * as Constants from "../../../constants";
import {CardContext, IContext} from "../../../types/document";
import {IUserState} from "../../../types/redux/user";

/* Bootstrap*/
import {Button, ButtonGroup, Dropdown, OverlayTrigger, Tooltip} from "react-bootstrap";

/* Redux */
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../redux";
import {IResultsState, ISelected} from "../../../types/redux/result";
import {clearSelectedResults} from "../../../redux/slices/result-slice";
import {AnyAction} from "@reduxjs/toolkit";

/* Components */
import Save from "../components/save";
import DownloadDropdown from "../components/download-dropdown";
import PageToolsKebab from "../components/page-tools-kebab";
import NotifyModal from "../../modal/notify-modal";
import ShareModal from "../../modal/share-modal";
import LoginPromptModal from "../../modal/login-prompt-modal";

/* API */
import DownloadService from "../../../api/download";
import useError from "../../../hooks/useError";

interface IProps {
  context: string
}

const CardTools = (props: IProps):JSX.Element => {
  const {
    context// {toolbar, card }
  } = props;

  /* Context */
  const cardContext: any = useContext<IContext>(CardContext).context; // {bibliography, document}
  const selectedResults: ISelected[] = useSelector<RootState, IResultsState>(state => state.result).selected_ids;

  /* app state */
  const resultState: IResultsState = useSelector<RootState, IResultsState>(state => state.result);
  const userState: IUserState = useSelector<RootState, IUserState>(state => state.user);

  /* state */
  const [pendingLogin, setPendingLogin] = useState<boolean>(false);
  const [loginPromptModal, setLoginPromptModal] = useState<boolean>(false);
  const [shareModalShow, setShareModalShow] = useState<boolean>(false);
  const [notifyModal, setNotifyModal] = useState<boolean>(false);

  /* Hooks */
  const dispatch:Dispatch<AnyAction> = useDispatch();
  const {handleError} = useError();

  useEffect(()=>{
    if (pendingLogin && userState.loggedIn) {
      setPendingLogin(false);
      handleDownloadClick();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userState.loggedIn])

  const handleDownloadClick = (): void => {
    if (userState.loggedIn) {
      DownloadService.bulkDownload(resultState.selected_ids.map((selected : ISelected) => selected.id)).then(()=>{
        dispatch(clearSelectedResults())
        setNotifyModal(true);
      }).catch(e=> handleError(e))
    } else {
      setPendingLogin(true);
      setLoginPromptModal(true)
    }
  }

  return (
    <div className={'card-tools p-0'}>
      <ButtonGroup aria-label="Card Tools">
        {/* Download - Documents only */}
        {(cardContext === Constants.DOCUMENT) &&
          <div className={'download-btns ms-lg-3 me-1'}>
            {(context === Constants.TOOLBAR) && (selectedResults.length === 0) &&
              <OverlayTrigger overlay={
                <Tooltip className={(selectedResults.length > 0) ? 'hidden' : ''}>{Constants.TOOLTIP_PAGE_PAGE_TOOLS}</Tooltip>
              }>
                <span> {/* enable bootstrap tooltip to display when button is disabled */}
                  <Dropdown.Toggle
                    disabled={(context === Constants.TOOLBAR) && (selectedResults.length === 0)}
                    variant={'success'}
                    className={'btn btn-sm btn-light mt-0'}
                  >
                    <i className={'bi bi-download'}/>
                    <span className={'ms-2 me-1 text-smaller'}>Download</span>
                  </Dropdown.Toggle>
               </span>
            </OverlayTrigger>}

            {/* One document: download artifacts */}
            {((selectedResults.length === 1) || (context === Constants.CARD)) &&
              <DownloadDropdown context={context}/>}

            {/* More than one document: bulk download */}
            {(context === Constants.TOOLBAR) &&
              (selectedResults.length > 1) &&
              <Button
                className={'btn btn-sm btn-light mt-0'}
                onClick={()=>handleDownloadClick()}
              >
                <i className={'bi bi-download'}/>
                <span className={'ms-2 me-1 text-smaller'}>Bulk download</span>
              </Button>}
          </div>}

        {/* Share */}
        {((cardContext === Constants.BIBLIOGRAPHY) ||
          (cardContext === Constants.COLLECTION)) &&  /*  ||
          (cardContext === Constants.SEARCH)*/
            <Button
              className={'share-btn btn btn-sm btn-light rounded mt-0 ms-3 text-smaller'}
              onClick={() => setShareModalShow(true)}
              disabled={(context === Constants.TOOLBAR) ? (selectedResults.length === 0) : false}
            >
              <i className={'bi bi-share'}/>
              <span className={'ms-2 me-1'}>Share</span>
            </Button>}

        {/* Save (Add to Library) */}
        <div className={(context === Constants.TOOLBAR) ? 'd-none d-sm-block' : ''}>
          {((cardContext === Constants.DOCUMENT) ||
              (cardContext === Constants.BIBLIOGRAPHY) ||
              (cardContext === Constants.SEARCH)) &&
            <div className={'mt-1'}>
              <Save context={context}/>
            </div>}
        </div>


        {/* Kebab Menu */}
        <PageToolsKebab context={context}/>

      </ButtonGroup>

      {/* Save (Add to Library) */}
      <div className={(context === Constants.TOOLBAR) ? 'd-sm-none' : 'd-none'}>
        {((cardContext === Constants.DOCUMENT) ||
            (cardContext === Constants.BIBLIOGRAPHY) ||
            (cardContext === Constants.SEARCH)) &&
          <div className={'mt-1'}>
            <Save context={context}/>
          </div>}
      </div>

      <ShareModal
        show={shareModalShow}
        onHide={() => setShareModalShow(false)}
      />

      <NotifyModal
        title={'Bulk Download'}
        message={'Your selected files are being processed. Check your email for instructions to download.'}
        show={notifyModal}
        onHide={() => setNotifyModal(false)}
      />

      {loginPromptModal && <LoginPromptModal
        title={'Bulk Download'}
        show={loginPromptModal}
        onHide={()=>setLoginPromptModal(false)}/>}
    </div>
  )
}
export default CardTools;
